import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';
import { CUSTOM_CROSSHAIR } from './useMeasureTool';

export type DrawGeometryType = 'Point' | 'Line' | 'Polygon';

export interface DrawnGeometry {
  type: 'Point' | 'LineString' | 'Polygon';
  coordinates: number[] | number[][] | number[][][];
}

/** Colours used to draw the in-progress sketch. */
export interface DrawStyle {
  /** Line stroke + polygon outline + the dot/vertex fill. */
  stroke: string;
  /** Polygon fill colour. */
  fill: string;
  /** Polygon fill opacity (0..1). */
  fillAlpha: number;
}

interface UseCreateFeatureDrawArgs {
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  /** True while the Create Feature tool is in drawing mode. */
  active: boolean;
  /** The chosen geometry type, or null before one is selected. */
  geometryType: DrawGeometryType | null;
  /** Sketch colours — orange for a new layer, the target layer's symbology when appending. */
  style?: DrawStyle;
  /** When set, the hook loads this geometry as editable nodes (edit existing feature). */
  editGeometry?: DrawnGeometry | null;
  /** Fired with the completed GeoJSON geometry (EPSG:4326 lon/lat). */
  onComplete: (geometry: DrawnGeometry) => void;
  /**
   * Asked on a fresh click (no in-progress geometry): is there an existing
   * feature near (lng,lat) within `tolMeters` to open for editing? Resolve true
   * if one was loaded (the hook then skips adding a vertex), false otherwise.
   */
  onRequestEditAt?: (lng: number, lat: number, tolMeters: number) => Promise<boolean>;
}

// US-104113 Draw Features. A dedicated data source keeps the in-progress sketch
// off `viewer.entities`, so layer highlight/refresh (which calls
// `entities.removeAll()`) never wipes it.
const DRAW_DATA_SOURCE = 'create-feature-draw';
// Default sketch symbology: orange line + dot (US — new-layer drawing). When
// appending to an existing layer the caller passes the layer's own symbology.
const DEFAULT_DRAW_STYLE: DrawStyle = { stroke: '#ff6600', fill: '#ff6600', fillAlpha: 0.5 };

// The dot used for both editable vertices and the frozen point preview: sketch
// colour with a dark outline so it stays visible over any basemap.
const sketchDot = (stroke: string): Cesium.PointGraphics.ConstructorOptions => ({
  pixelSize: 11,
  color: Cesium.Color.fromCssColorString(stroke),
  outlineColor: Cesium.Color.fromCssColorString('#333333'),
  outlineWidth: 2,
  disableDepthTestDistance: Number.POSITIVE_INFINITY,
});

/**
 * Point/Line/Polygon drawing for the Create Feature tool (US-104113).
 * - Point: one left-click completes immediately.
 * - Line: left-click adds vertices; double-click completes (>= 2 vertices).
 * - Polygon: left-click adds vertices; double-click completes + auto-closes (>= 3).
 * A rubber-band edge tracks the cursor; the cursor is a crosshair while active.
 */
export const useCreateFeatureDraw = ({
  viewerRef,
  active,
  geometryType,
  style,
  editGeometry,
  onComplete,
  onRequestEditAt,
}: UseCreateFeatureDrawArgs) => {
  const [isDrawing, setIsDrawing] = useState(false);

  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const pointsRef = useRef<Cesium.Cartesian3[]>([]);
  const mouseRef = useRef<Cesium.Cartesian3 | null>(null);
  const onRequestEditAtRef = useRef(onRequestEditAt);
  onRequestEditAtRef.current = onRequestEditAt;
  // Live (non-stale) view of editGeometry so the click handler can tell it's in
  // edit mode even right after a pick, before the effect re-runs with the new prop.
  const editGeometryRef = useRef(editGeometry);
  editGeometryRef.current = editGeometry;
  // Vertex marker entities, kept parallel to pointsRef so a picked marker maps
  // to its index for editing (drag/delete — US-108778).
  const vertexEntitiesRef = useRef<Cesium.Entity[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const ctxMenuCleanupRef = useRef<(() => void) | null>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const styleRef = useRef<DrawStyle>(style ?? DEFAULT_DRAW_STYLE);
  styleRef.current = style ?? DEFAULT_DRAW_STYLE;

  // `dataSources.add()` only pushes into the collection on a later microtask, so
  // a `getByName` right after it still comes back empty and we'd create a SECOND
  // data source. The sketch then splits across two of them and every cleanup
  // (finish / teardown / clearDrawn) hits the wrong one, leaving stale vertex
  // dots on the map. Hold the instance in a ref so every lookup is the same one.
  const dsRef = useRef<{ viewer: Cesium.Viewer; ds: Cesium.CustomDataSource } | null>(null);

  const getDataSource = useCallback((viewer: Cesium.Viewer): Cesium.CustomDataSource => {
    const cached = dsRef.current;
    if (cached && cached.viewer === viewer) return cached.ds;
    const existing = viewer.dataSources.getByName(DRAW_DATA_SOURCE)[0] as Cesium.CustomDataSource | undefined;
    const ds = existing ?? new Cesium.CustomDataSource(DRAW_DATA_SOURCE);
    if (!existing) viewer.dataSources.add(ds);
    dsRef.current = { viewer, ds };
    return ds;
  }, []);

  /** The sketch data source for this viewer, if one has been created yet. */
  const peekDataSource = useCallback((viewer: Cesium.Viewer): Cesium.CustomDataSource | null => {
    const cached = dsRef.current;
    return cached && cached.viewer === viewer ? cached.ds : null;
  }, []);

  const toLngLat = (p: Cesium.Cartesian3): [number, number] => {
    const c = Cesium.Cartographic.fromCartesian(p);
    return [Cesium.Math.toDegrees(c.longitude), Cesium.Math.toDegrees(c.latitude)];
  };

  const clearSketch = useCallback(
    (viewer: Cesium.Viewer) => {
      getDataSource(viewer).entities.removeAll();
      pointsRef.current = [];
      vertexEntitiesRef.current = [];
      mouseRef.current = null;
      dragIndexRef.current = null;
      suppressClickRef.current = false;
    },
    [getDataSource],
  );

  /** Discard an in-progress (or completed-but-unsaved) sketch. */
  const clearDrawn = useCallback(() => {
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) clearSketch(viewer);
  }, [viewerRef, clearSketch]);

  // Build a GeoJSON geometry from the current vertices (null if below minimum).
  const buildGeometry = (type: DrawGeometryType): DrawnGeometry | null => {
    const pts = pointsRef.current;
    if (type === 'Point') return pts.length >= 1 ? { type: 'Point', coordinates: toLngLat(pts[0]) } : null;
    if (type === 'Line') return pts.length >= 2 ? { type: 'LineString', coordinates: pts.map(toLngLat) } : null;
    if (pts.length < 3) return null;
    const ring = pts.map(toLngLat);
    ring.push(ring[0]); // auto-close (US-104113 Sc5)
    return { type: 'Polygon', coordinates: [ring] };
  };

  // The current geometry from the (possibly edited) vertices — used on Save.
  const getGeometry = useCallback((): DrawnGeometry | null => {
    if (!geometryType) return null;
    return buildGeometry(geometryType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometryType]);

  // Emit the completed geometry. The cursor point is dropped so the rendered
  // sketch freezes to the final shape (stays visible through the attributes step).
  const finish = useCallback((type: DrawGeometryType) => {
    mouseRef.current = null;
    const viewer = viewerRef.current;
    if (viewer && !viewer.isDestroyed()) {
      const ds = peekDataSource(viewer);
      if (ds) {
        vertexEntitiesRef.current.forEach((e) => {
          try {
            ds.entities.remove(e);
          } catch (err) {}
        });
        // A line/polygon still has its rubber-band preview once the editable
        // nodes go, but a point has nothing left to draw. Leave a static marker
        // so the placed point stays visible through the attributes step — it's
        // untracked (not in vertexEntitiesRef), so teardown leaves it alone and
        // only clearDrawn() removes it once the feature is saved.
        const placed = pointsRef.current[0];
        if (type === 'Point' && placed) {
          ds.entities.add({ position: placed, point: sketchDot(styleRef.current.stroke) });
        }
      }
      vertexEntitiesRef.current = [];
    }
    const geom = buildGeometry(type);
    if (geom) onCompleteRef.current(geom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cartesian vertices for an (already single-part) GeoJSON geometry, used to
  // seed the editable nodes when opening an existing feature.
  const verticesFromGeometry = (geom: DrawnGeometry): Cesium.Cartesian3[] => {
    const toCart = (c: number[]) => Cesium.Cartesian3.fromDegrees(c[0], c[1]);
    if (geom.type === 'Point') return [toCart(geom.coordinates as number[])];
    if (geom.type === 'LineString') return (geom.coordinates as number[][]).map(toCart);
    const ring = ((geom.coordinates as number[][][])[0] ?? []).map(toCart);
    // drop the closing duplicate so editing sees distinct nodes
    if (ring.length > 1 && Cesium.Cartesian3.equals(ring[0], ring[ring.length - 1])) ring.pop();
    return ring;
  };

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    // Detach the drawing handler + restore the cursor. The filled SHAPE preview
    // stays visible through the attributes step (via pointsRef), but the
    // editable vertex DOTS are removed — they only belong while actively
    // drawing/editing (active), so they don't linger after a feature is finished.
    // Everything is wiped on an explicit clearDrawn() (save / close / add-another).
    const teardown = () => {
      handlerRef.current?.destroy();
      handlerRef.current = null;
      ctxMenuCleanupRef.current?.();
      ctxMenuCleanupRef.current = null;
      dragIndexRef.current = null;
      if (!viewer.isDestroyed()) {
        viewer.scene.canvas.style.cursor = '';
        viewer.scene.screenSpaceCameraController.enableInputs = true; // re-enable if a drag was mid-flight
        const ds = peekDataSource(viewer);
        if (ds) {
          vertexEntitiesRef.current.forEach((e) => {
            try {
              ds.entities.remove(e);
            } catch {
              /* already gone */
            }
          });
        }
        vertexEntitiesRef.current = [];
        // Restore Cesium's default double-click (track picked entity) that we
        // suppressed while drawing, so other tools keep that behaviour.
        viewer.screenSpaceEventHandler.setInputAction((e: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
          const picked = viewer.scene.pick(e.position);
          if (Cesium.defined(picked) && picked.id instanceof Cesium.Entity) viewer.trackedEntity = picked.id;
        }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
      }
      setIsDrawing(false);
    };

    if (!active || !geometryType) {
      teardown();
      return;
    }

    clearSketch(viewer); // fresh session — discard any prior committed sketch
    handlerRef.current?.destroy();
    const ds = getDataSource(viewer);
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current = handler;
    viewer.scene.canvas.style.cursor = CUSTOM_CROSSHAIR;
    setIsDrawing(true);

    // Suppress Cesium's default double-click "fly to / track entity" — our
    // vertices are entities, so double-clicking to FINISH a line/polygon would
    // otherwise zoom to the picked vertex. Restored on teardown.
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    // Suppress the browser context menu over the canvas so a right-click to
    // delete a node (US-108778) isn't swallowed by the OS menu (esp. macOS
    // ctrl-click / two-finger tap). Restored on teardown.
    const onContextMenu = (e: Event) => e.preventDefault();
    viewer.scene.canvas.addEventListener('contextmenu', onContextMenu);
    ctxMenuCleanupRef.current = () => viewer.scene.canvas.removeEventListener('contextmenu', onContextMenu);

    const sketchColor = () => Cesium.Color.fromCssColorString(styleRef.current.stroke);
    const pick = (pos: Cesium.Cartesian2) => viewer.camera.pickEllipsoid(pos, viewer.scene.globe.ellipsoid);

    const addVertexMarker = (position: Cesium.Cartesian3) => {
      const entity = ds.entities.add({
        position,
        point: sketchDot(styleRef.current.stroke),
      });
      vertexEntitiesRef.current.push(entity);
    };

    // Nearest vertex within a pixel threshold of the cursor, or -1. Distance in
    // screen space (like the polygon-close test) is far more forgiving than
    // scene.pick on a small dot — important for trackpad ctrl-click on macOS.
    const VERTEX_HIT_PX = 16;
    const pickVertexIndex = (windowPos: Cesium.Cartesian2): number => {
      const picked = viewer.scene.pick(windowPos);
      if (Cesium.defined(picked) && picked.id) {
        const found = vertexEntitiesRef.current.indexOf(picked.id);
        if (found !== -1) return found;
      }
      for (let i = 0; i < pointsRef.current.length; i++) {
        const win = Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, pointsRef.current[i]);
        if (win && Cesium.Cartesian2.distance(win, windowPos) <= VERTEX_HIT_PX) return i;
      }
      return -1;
    };

    // Lazily-created rubber-band preview (line for Line; closed outline + fill
    // for Polygon). Colours are read live from styleRef so they match the
    // target layer's symbology (or stay orange for a new layer).
    let previewLine: Cesium.Entity | null = null;
    let closingLine: Cesium.Entity | null = null;
    let previewPolygon: Cesium.Entity | null = null;
    const ensurePreview = () => {
      if (geometryType === 'Point') return;
      const ds = getDataSource(viewer);
      const strokeColor = sketchColor();
      const fillColor = Cesium.Color.fromCssColorString(styleRef.current.fill).withAlpha(styleRef.current.fillAlpha);

      // 1. The path connecting placed vertices (Solid Line)
      if (!previewLine) {
        previewLine = ds.entities.add({
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              const pts = [...pointsRef.current];
              const isDrawingActive = mouseRef.current && !editGeometryRef.current;
              // Trail the mouse only when drawing a new feature
              if (isDrawingActive) {
                // While actively drawing, connect to the mouse cursor
                pts.push(mouseRef.current!);
              } else if (geometryType === 'Polygon' && pts.length >= 3) {
                // Once finished (mouse is null), close the polygon with a solid line
                pts.push(pts[0]);
              }
              return pts;
            }, false),
            width: 2,
            material: strokeColor,
            clampToGround: false,
          },
        });
      }

      if (geometryType === 'Polygon') {
        // 2. Indicator from first vertex to mouse (Dotted Line)
        // This provides the "closing" visual cue found in the measure tool
        if (!closingLine) {
          closingLine = ds.entities.add({
            polyline: {
              positions: new Cesium.CallbackProperty(() => {
                const isDrawingActive = mouseRef.current && !editGeometryRef.current;
                // Only show dotted line if drawing is active and we have at least one point
                if (pointsRef.current.length > 0 && isDrawingActive) {
                  return [pointsRef.current[0], mouseRef.current!];
                }
                return [];
              }, false),
              width: 2,
              material: new Cesium.PolylineDashMaterialProperty({
                color: strokeColor,
                dashLength: 8.0,
              }),
              clampToGround: false,
            },
          });
        }

        // 3. Shaded fill area
        if (!previewPolygon) {
          previewPolygon = ds.entities.add({
            polygon: {
              hierarchy: new Cesium.CallbackProperty(() => {
                const pts = [...pointsRef.current];
                const isDrawingActive = mouseRef.current && !editGeometryRef.current;
                if (isDrawingActive) {
                  pts.push(mouseRef.current!);
                }
                return pts.length >= 3 ? new Cesium.PolygonHierarchy(pts) : new Cesium.PolygonHierarchy([]);
              }, false),
              material: fillColor,
            },
          });
        }
      }
    };

    // Edit existing feature (US-108778): seed the loaded geometry as editable
    // nodes — drag/right-click-delete work; clicks don't add/replace.
    if (editGeometry) {
      pointsRef.current = verticesFromGeometry(editGeometry);
      pointsRef.current.forEach((p) => addVertexMarker(p));
      if (geometryType !== 'Point') ensurePreview();
      viewer.scene.canvas.style.cursor = '';
    }

    handler.setInputAction(async (click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      // A click that ended a vertex drag must not also append a new vertex.
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (editGeometryRef.current) return; // editing existing geometry — use drag/delete, not click-to-add
      const cartesian = pick(click.position);
      if (!cartesian) return;

      // When starting fresh, a click near an existing saved feature opens it for
      // editing (Figma: "Click on an existing feature to edit"). The modal does a
      // tolerant backend lookup (tolerance ≈ 12px in m).
      //
      // Non-blocking: never stall vertex placement on the network. Fire the pick
      // in the background; if a feature is found the modal sets `editGeometry`
      // and this effect re-inits the sketch (clearing any optimistic vertex).
      // If nothing is nearby the vertex we place below simply stays.
      if (pointsRef.current.length === 0 && onRequestEditAtRef.current && !editGeometryRef.current) {
        const [lng, lat] = toLngLat(cartesian);
        const near = pick(new Cesium.Cartesian2(click.position.x + 12, click.position.y));
        const tolMeters = near ? Cesium.Cartesian3.distance(cartesian, near) : 20;
        void onRequestEditAtRef.current(lng, lat, tolMeters);
      }
      // If edit mode flipped on during an in-flight pick from a prior click, don't
      // also append a vertex on this one.
      if (editGeometryRef.current) return;

      if (geometryType === 'Point') {
        // One click = one point; replace any previous point.
        const vertexIdx = pickVertexIndex(click.position);
        if (pointsRef.current.length === 1 && vertexIdx === 0) {
          finish('Point');
          return;
        }
        if (pointsRef.current.length === 0) {
          pointsRef.current = [cartesian];
          addVertexMarker(cartesian);
        }
        return;
      }

      if (geometryType === 'Polygon' && pointsRef.current.length >= 3) {
        if (pickVertexIndex(click.position) === 0) {
          finish('Polygon');
          return;
        }
      }

      pointsRef.current.push(cartesian);
      addVertexMarker(cartesian);
      ensurePreview();
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Drag a node to move it — works for any type (incl. repositioning a point
    // in edit mode, US-108778 Sc1).
    handler.setInputAction((down: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const idx = pickVertexIndex(down.position);
      if (idx < 0) return;
      dragIndexRef.current = idx;
      mouseRef.current = null;
      viewer.scene.screenSpaceCameraController.enableInputs = false;
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    handler.setInputAction(() => {
      dragIndexRef.current = null;
      viewer.scene.screenSpaceCameraController.enableInputs = true;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    if (geometryType !== 'Point') {
      handler.setInputAction(() => {
        // A double-click delivers two LEFT_CLICKs first, so the last vertex is a
        // duplicate of the previous — drop it before finishing.
        if (geometryType !== 'Line') return;
        const pts = pointsRef.current;
        if (pts.length >= 2 && Cesium.Cartesian3.equals(pts[pts.length - 1], pts[pts.length - 2])) {
          pts.pop();
          vertexEntitiesRef.current.pop();
        }
        finish('Line');
      }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    }
    // US-108778 Sc5 — right-click a vertex to delete it. Geometry that drops
    // below the minimum stays visible/editable; save validation handles it.
    handler.setInputAction((evt: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      const idx = pickVertexIndex(evt.position);
      if (idx < 0) return;
      const [removed] = vertexEntitiesRef.current.splice(idx, 1);
      if (removed) ds.entities.remove(removed);
      pointsRef.current.splice(idx, 1);
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      // Dragging a vertex — move it (and its marker) live.
      if (dragIndexRef.current !== null) {
        suppressClickRef.current = true;
        const dragged = pick(movement.endPosition);
        if (!dragged) return;
        const i = dragIndexRef.current;
        pointsRef.current[i] = dragged;
        const entity = vertexEntitiesRef.current[i];
        if (entity) entity.position = new Cesium.ConstantPositionProperty(dragged);
        return;
      }
      // No rubber-band cursor trail when editing an existing feature (the
      // geometry is complete) or for points.
      if (geometryType === 'Point' || editGeometryRef.current || pointsRef.current.length === 0) return;
      mouseRef.current = pick(movement.endPosition) ?? null;
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    return teardown;
  }, [active, geometryType, editGeometry, viewerRef, getDataSource, peekDataSource, clearSketch, finish]);

  // Safety net: detach handler + remove the data source on unmount.
  useEffect(() => {
    return () => {
      const viewer = viewerRef.current;
      handlerRef.current?.destroy();
      if (viewer && !viewer.isDestroyed()) {
        const ds = peekDataSource(viewer);
        if (ds) viewer.dataSources.remove(ds, true);
      }
      dsRef.current = null;
    };
  }, [viewerRef, peekDataSource]);

  return { isDrawing, clearDrawn, getGeometry };
};
